import { Link } from "@remix-run/react";
import { MapPin } from "lucide-react";
import * as React from "react";
import { Badge } from "#app/components/ui/badge.tsx";
import { formatState } from "#app/utils/format-text.tsx";
import { getMapImgSrc } from "#app/utils/misc.tsx";

// Helper functions (consider moving to a shared utils file)
export function getStatusVariant(
  iconType: string,
): "default" | "bg-yellow-500" | "bg-red-500" | "bg-green-500" | "bg-blue-500" {
  switch (iconType) {
    case "A":
    case "B":
      return "bg-yellow-500";
    case "AR":
    case "BR":
    case "GR":
    case "YR":
      return "bg-red-500";
    case "G":
      return "bg-green-500";
    case "Y":
      return "bg-blue-500";
    default:
      return "default";
  }
}

export function getStatusText(iconType: string): string {
  switch (iconType) {
    case "A":
    case "B":
      return "uso restrito";
    case "AR":
    case "BR":
    case "GR":
    case "YR":
      return "em manutenção";
    case "G":
      return "carregador público";
    case "Y":
      return "carregador rápido";
    default:
      return "";
  }
}

export function getResponsiveImageSrcSet(
  stationId: number,
  isSheet: boolean,
): string {
  if (isSheet) {
    return `
      https://carregados.com.br/cdn-cgi/image/format=auto,height=320,width=768,fit=crop${getMapImgSrc(
        stationId,
      )} 2x,
      https://carregados.com.br/cdn-cgi/image/format=auto,height=480,width=1152,fit=crop${getMapImgSrc(
        stationId,
      )} 3x
    `;
  } else {
    return `
      https://carregados.com.br/cdn-cgi/image/format=auto,height=288,fit=crop,width=320${getMapImgSrc(
        stationId,
      )} 320w,
      https://carregados.com.br/cdn-cgi/image/format=auto,height=288,fit=crop,width=640${getMapImgSrc(
        stationId,
      )} 640w,
      https://carregados.com.br/cdn-cgi/image/format=auto,height=288,fit=crop,width=960${getMapImgSrc(
        stationId,
      )} 960w,
      https://carregados.com.br/cdn-cgi/image/format=auto,height=288,fit=crop,width=1280${getMapImgSrc(
        stationId,
      )} 1280w,
      https://carregados.com.br/cdn-cgi/image/format=auto,height=288,fit=crop,width=2560${getMapImgSrc(
        stationId,
      )} 2560w
    `;
  }
}

// Define props
interface StationHeaderProps {
  station: {
    id: number;
    name: string;
    city: string | null;
    state: string | null;
    icon_type: string;
    isInactive: boolean;
    hasMap: boolean;
    reverseGeocodedAddressComponents: Array<{
      administrativeArea1: string | null;
      administrativeArea2: string | null;
    }>;
  } | null;
  isSheet: boolean;
}

export function StationHeader({ station, isSheet }: StationHeaderProps) {
  if (!station) {
    return null; // Or a loading/placeholder state
  }

  const locationText =
    station.city && station.state
      ? `${station.city} - ${formatState(station.state).replaceAll(" ()", "")}`
      : station.reverseGeocodedAddressComponents[0]?.administrativeArea2 &&
          station.reverseGeocodedAddressComponents[0]?.administrativeArea1
        ? `${station.reverseGeocodedAddressComponents[0]?.administrativeArea2} - ${station.reverseGeocodedAddressComponents[0]?.administrativeArea1}`
        : null;

  return (
    <div className="w-full overflow-hidden bg-gradient-to-b from-white via-white to-gray-50 shadow-sm dark:from-gray-900 dark:via-gray-900 dark:to-gray-950">
      <div className="py-6 px-3">
        <div className="mb-4">
          <div className="flex items-center gap-2">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Estação de Carregamento para Carros Elétricos
            </p>
          </div>
          <h1 className="mt-2 text-3xl font-bold tracking-tight dark:text-white">
            {station.name
              .replaceAll("Estação", "")
              .replaceAll(/\(.*?\)/g, "")
              .trim()}
          </h1>
        </div>

        <div className="flex flex-row flex-wrap items-center justify-between gap-3">
          {locationText && (
            <div className="flex items-center gap-2">
              <div className="flex items-center border border-gray-100 rounded-full bg-muted/30 px-3 py-1.5 backdrop-blur-sm dark:bg-gray-800/50">
                <MapPin className="mr-1.5 h-3.5 w-3.5 text-muted-foreground" />
                <p className="text-sm font-medium capitalize text-muted-foreground">
                  {locationText}
                </p>
              </div>
            </div>
          )}

          {station.isInactive ? (
            <Badge
              variant="destructive"
              className="px-3 py-1.5 text-xs font-semibold uppercase shadow-sm"
            >
              INATIVA
            </Badge>
          ) : (
            <Badge
              variant="outline"
              className={`px-3 py-1.5 text-xs font-semibold uppercase text-white shadow-sm ${getStatusVariant(
                station.icon_type,
              )}`}
            >
              {getStatusText(station.icon_type)}
            </Badge>
          )}
        </div>
      </div>

      {/* Map Image - Conditionally rendered based on isSheet and hasMap */}
      {!isSheet && station.id && station.hasMap && (
        <div className="relative h-full w-full overflow-hidden px-2">
          <Link to={`/mapa?estacao=${station.id}`} reloadDocument>
            <img
              src={`https://carregados.com.br/cdn-cgi/image/format=auto,height=${isSheet ? "160" : "288"},width=${isSheet ? "384" : "960"},fit=crop${getMapImgSrc(
                station.id,
              )}`}
              srcSet={getResponsiveImageSrcSet(station.id, isSheet)}
              className="h-full w-full object-cover hover:opacity-80 md:rounded-lg"
              alt={`Mapa da estação ${station.name}`}
            />
          </Link>
        </div>
      )}
    </div>
  );
}
